import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './stores/auth';
import { connectSocket, disconnectSocket } from './utils/api';
import { WalletProvider } from './contexts/WalletContext';

// Layout
import Layout from './components/Layout';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Templates from './pages/Templates';
import Strategies from './pages/Strategies';
import Models from './pages/Models';
import Datasets from './pages/Datasets';
import Trading from './pages/Trading';
import Bots from './pages/Bots';
import Wallets from './pages/Wallets';
import WorkflowEditor from './pages/WorkflowEditor';
import Chat from './pages/Chat';
import Settings from './pages/Settings';
import Backtest from './pages/Backtest';
import DeFi from './pages/DeFi';
import TokenMetrics from './pages/TokenMetrics';
import Scanner from './pages/Scanner';
import Portfolio from './pages/Portfolio';
import Alerts from './pages/Alerts';
import Deposit from './pages/Deposit';
import Withdraw from './pages/Withdraw';
import History from './pages/History';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30000,
    },
  },
});

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { token } = useAuthStore();
  
  useEffect(() => {
    if (token) {
      connectSocket();
    } else {
      disconnectSocket();
    }
  }, [token]);
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { token } = useAuthStore();
  if (token) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <WalletProvider>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            }
          />

          {/* Private routes with Layout */}
          <Route
            path="/"
            element={
              <PrivateRoute>
                <Layout />
              </PrivateRoute>
            }
          >
            <Route index element={<Dashboard />} />
            {/* Trading */}
            <Route path="trade" element={<Trading />} />
            <Route path="trade/:pair" element={<Trading />} />
            <Route path="bots" element={<Bots />} />
            <Route path="wallets" element={<Wallets />} />
            <Route path="deposit" element={<Deposit />} />
            <Route path="withdraw" element={<Withdraw />} />
            <Route path="history" element={<History />} />
            <Route path="defi" element={<DeFi />} />
            <Route path="token-metrics" element={<TokenMetrics />} />
            <Route path="scanner" element={<Scanner />} />
            <Route path="portfolio" element={<Portfolio />} />
            <Route path="alerts" element={<Alerts />} />
            <Route path="chat" element={<Chat />} />
            {/* Automation */}
            <Route path="strategies" element={<Strategies />} />
            <Route path="templates" element={<Templates />} />
            <Route path="workflow/:id" element={<WorkflowEditor />} />
            <Route path="backtest" element={<Backtest />} />
            {/* AI */}
            <Route path="models" element={<Models />} />
            <Route path="datasets" element={<Datasets />} />
            {/* Settings */}
            <Route path="settings" element={<Settings />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </WalletProvider>

      {/* Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#1e2329',
            color: '#eaecef',
            border: '1px solid rgba(255, 255, 255, 0.06)',
            borderRadius: '12px',
            padding: '12px 16px',
          },
          success: {
            iconTheme: {
              primary: '#0ecb81',
              secondary: '#1e2329',
            },
          },
          error: {
            iconTheme: {
              primary: '#f6465d',
              secondary: '#1e2329',
            },
          },
        }}
      />
    </QueryClientProvider>
  );
}
